import { createAsyncThunk } from "@reduxjs/toolkit";
import { MARKETING } from "constants/api";
import { toMarketingPage } from "utils";
import axios from "utils/http";

export interface IBatchListResponse extends ApiResponse {
  data: IVoucherBatchList;
}

export interface IBatchDetailsResponse extends ApiResponse {
  data: IVoucherBatchDetails;
}

export interface IVoucherListResponse extends ApiResponse {
  data: IVoucherList;
}

export interface IDeliveryListResponse extends ApiResponse {
  data: IVoucherDeliveryList;
}

export interface IIssueBatchResponse extends ApiResponse {
  data: IIssueBatchResult;
}

export interface IPartnerOptionsResponse extends ApiResponse {
  data: IMarketingPartnerOption[];
}

export interface IVoucherMatchResponse extends ApiResponse {
  data: IVoucherMatchResult;
}

export interface IEmailPreviewResponse extends ApiResponse {
  data: IVoucherEmailPreview;
}

export interface IUserSearchResponse extends ApiResponse {
  data: IRawMisanUser[];
}

interface IRawMisanUser {
  id: number;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone_country_code?: string | null;
  phone_number?: string | null;
}

export interface IPagedArgs extends ReduxAction {
  page: number;
  queries?: Record<string, string | number | undefined>;
}

export interface IIdArgs extends ReduxAction {
  id: number;
}

export interface IBatchArgs extends ReduxAction {
  id: number;
  page: number;
}

export interface IBatchSummaryArgs extends ReduxAction {
  id: number;
  label: string;
}

export const BATCH_VOUCHERS_PER_PAGE = 20;

export interface IUserSearchArgs extends ReduxAction {
  search: string;
}

export interface IIssueBatchArgs extends ReduxAction {
  payload: IIssueBatchPayload;
  idempotencyKey: string;
}

export interface IVoucherMatchArgs extends ReduxAction {
  values: string[];
}

export interface IEmailPreviewArgs extends ReduxAction {
  partnerId: number;
  amountNaira: string;
  expiresAt: string | null;
}

export const MATCH_VALUES_MAX = 5000;

const toParams = (
  page: number,
  queries: Record<string, string | number | undefined> = {}
) =>
  Object.entries(queries).reduce<Record<string, string | number>>(
    (params, [key, value]) => {
      if (value !== undefined && value !== "") params[key] = value;
      return params;
    },
    { page }
  );

const toRecipient = (user: IRawMisanUser): IVoucherRecipient => ({
  id: user.id,
  name: [user.first_name, user.last_name]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" "),
  email: user.email ?? "",
  phone: [user.phone_country_code, user.phone_number]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" "),
});

export const fetchVoucherBatches = createAsyncThunk(
  MARKETING.BATCHES,
  async ({ page, queries, onFailure }: IPagedArgs, { rejectWithValue }) => {
    try {
      const { data }: IBatchListResponse = await axios.get(MARKETING.BATCHES, {
        params: toParams(page, queries),
      });
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchVoucherBatch = createAsyncThunk(
  MARKETING.BATCH_DETAIL,
  async ({ id, page, onFailure }: IBatchArgs, { rejectWithValue }) => {
    try {
      const { data }: IBatchDetailsResponse = await axios.get(
        MARKETING.BATCH_DETAIL.replace(":id", String(id)),
        { params: { page, per_page: BATCH_VOUCHERS_PER_PAGE } }
      );
      const batch: IVoucherBatchDetails = {
        ...data,
        vouchers: toMarketingPage<IBatchVoucher>(data?.vouchers),
      };
      return batch;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchVoucherBatchSummary = createAsyncThunk(
  `${MARKETING.BATCHES}/summary`,
  async ({ id, label, onFailure }: IBatchSummaryArgs, { rejectWithValue }) => {
    try {
      const { data }: IBatchListResponse = await axios.get(MARKETING.BATCHES, {
        params: { page: 1, per_page: 100, search: label },
      });
      return (data?.data ?? []).find((batch) => batch.id === id) ?? null;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const issueVoucherBatch = createAsyncThunk(
  `${MARKETING.BATCHES}/issue`,
  async (
    { payload, idempotencyKey, onSuccess, onFailure }: IIssueBatchArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IIssueBatchResponse = await axios.post(
        MARKETING.BATCHES,
        payload,
        { headers: { "Idempotency-Key": idempotencyKey } }
      );
      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const resendFailedEmails = createAsyncThunk(
  MARKETING.BATCH_RESEND_FAILED,
  async ({ id, onSuccess, onFailure }: IIdArgs, { rejectWithValue }) => {
    try {
      const { data }: ApiResponse & { data: { resent: number } } =
        await axios.post(MARKETING.BATCH_RESEND_FAILED.replace(":id", String(id)));
      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchVouchers = createAsyncThunk(
  MARKETING.VOUCHERS,
  async ({ page, queries, onFailure }: IPagedArgs, { rejectWithValue }) => {
    try {
      const { data }: IVoucherListResponse = await axios.get(
        MARKETING.VOUCHERS,
        { params: toParams(page, queries) }
      );
      return toMarketingPage<IBatchVoucher>(data);
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchVoucherDeliveries = createAsyncThunk(
  MARKETING.DELIVERIES,
  async ({ page, queries, onFailure }: IPagedArgs, { rejectWithValue }) => {
    try {
      const { data }: IDeliveryListResponse = await axios.get(
        MARKETING.DELIVERIES,
        { params: toParams(page, queries) }
      );
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const revealVoucherCode = createAsyncThunk(
  MARKETING.VOUCHER_REVEAL,
  async ({ id, onSuccess, onFailure }: IIdArgs, { rejectWithValue }) => {
    try {
      const { data }: ApiResponse & { data: { code: string } } =
        await axios.post(MARKETING.VOUCHER_REVEAL.replace(":id", String(id)));
      if (onSuccess) onSuccess(data?.code);
      return data?.code;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const resendVoucherEmail = createAsyncThunk(
  MARKETING.VOUCHER_RESEND,
  async ({ id, onSuccess, onFailure }: IIdArgs, { rejectWithValue }) => {
    try {
      const { message }: ApiResponse = await axios.post(
        MARKETING.VOUCHER_RESEND.replace(":id", String(id))
      );
      if (onSuccess) onSuccess(message);
      return id;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const searchVoucherRecipients = createAsyncThunk(
  MARKETING.USER_SEARCH,
  async ({ search, onSuccess, onFailure }: IUserSearchArgs, { rejectWithValue }) => {
    try {
      const { data }: IUserSearchResponse = await axios.get(
        MARKETING.USER_SEARCH,
        { params: { q: search } }
      );
      const users = data ?? [];
      const recipients = users.map(toRecipient);
      if (onSuccess) onSuccess(recipients);
      return recipients;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchVoucherPartnerOptions = createAsyncThunk(
  `${MARKETING.PARTNERS}/options`,
  async ({ onFailure }: ReduxAction, { rejectWithValue }) => {
    try {
      const { data }: IPartnerOptionsResponse = await axios.get(
        MARKETING.PARTNER_OPTIONS
      );
      return data ?? [];
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const matchVoucherRecipients = createAsyncThunk(
  MARKETING.USER_MATCH,
  async (
    { values, onSuccess, onFailure }: IVoucherMatchArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IVoucherMatchResponse = await axios.post(
        MARKETING.USER_MATCH,
        { values }
      );
      const result: IVoucherMatchResult = {
        matched: data?.matched ?? [],
        unmatched: data?.unmatched ?? [],
      };
      if (onSuccess) onSuccess(result);
      return result;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchVoucherEmailPreview = createAsyncThunk(
  MARKETING.BATCH_EMAIL_PREVIEW,
  async (
    { partnerId, amountNaira, expiresAt, onSuccess, onFailure }: IEmailPreviewArgs,
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string | number> = {
        partner_id: partnerId,
        amount_naira: amountNaira,
      };
      if (expiresAt) params.expires_at = expiresAt;

      const { data }: IEmailPreviewResponse = await axios.get(
        MARKETING.BATCH_EMAIL_PREVIEW,
        { params }
      );
      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);
